import { HorizonError } from './horizon-error'

const MAX_RETRIES = 3
const MAX_DELAY_MS = 10_000
const BASE_DELAY_MS = 1000

function parseRetryAfterMs(headers: Headers): number | null {
  const value = headers.get('Retry-After')
  if (!value) {
    return null
  }

  const seconds = Number(value)
  if (!Number.isNaN(seconds)) {
    return Math.max(0, seconds * 1000)
  }

  const date = Date.parse(value)
  if (!Number.isNaN(date)) {
    return Math.max(0, date - Date.now())
  }

  return null
}

function computeDelay(attempt: number, retryAfterMs: number | null): number {
  if (retryAfterMs !== null) {
    return Math.min(retryAfterMs, MAX_DELAY_MS)
  }
  const exponential = BASE_DELAY_MS * 2 ** attempt
  const jitter = Math.random() * BASE_DELAY_MS
  return Math.min(exponential + jitter, MAX_DELAY_MS)
}

export async function withRetryOn429<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal | null,
  maxRetries = MAX_RETRIES,
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // biome-ignore lint/performance/noAwaitInLoops: intentional sequential retry — each attempt must resolve before the next
      return await fn()
    } catch (error) {
      const isRateLimited =
        error instanceof HorizonError && error.status === 429
      const hasRetriesLeft = attempt < maxRetries

      if (!(isRateLimited && hasRetriesLeft) || signal?.aborted) {
        throw error
      }

      const retryAfterMs = parseRetryAfterMs(error.headers)
      const delay = computeDelay(attempt, retryAfterMs)
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }
  // Loop always exits via return (success) or throw (in catch block).
  // This satisfies the type checker for the theoretical case where
  // maxRetries < 0.
  throw new Error('Unreachable: max retries exceeded')
}
